import { useMemo, useState } from 'react';
import type { VendorPrice } from '../../types/models';
import { StarRating } from './StarRating';
import { Pagination } from './Pagination';

type SortKey = 'price' | 'rating';

interface VendorTableProps {
  title: string;
  description?: string;
  prices: VendorPrice[];
  pageSize?: number;
  className?: string;
}

function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('de-AT', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function VendorTable({
  title,
  description,
  prices,
  pageSize = 5,
  className = '',
}: VendorTableProps) {
  const [sort, setSort] = useState<SortKey>('price');
  const [page, setPage] = useState(1);

  const hasRatings = prices.some((p) => typeof p.rating === 'number');

  const sorted = useMemo(() => {
    const copy = [...prices];
    if (sort === 'rating') {
      copy.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
    } else {
      copy.sort((a, b) => a.priceCents - b.priceCents);
    }
    return copy;
  }, [prices, sort]);

  const bestPriceCents = prices.length ? Math.min(...prices.map((p) => p.priceCents)) : null;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page_ = Math.min(page, totalPages);
  const pageItems = sorted.slice((page_ - 1) * pageSize, page_ * pageSize);

  return (
    <div className={`panel p-5 ${className}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {description && <p className="text-sm text-ink-500 mt-1 max-w-md">{description}</p>}
        </div>
        {hasRatings && (
          <label className="text-xs text-ink-500 flex items-center gap-2 shrink-0">
            Sort by
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
              className="border border-ink-900/10 rounded-lg px-2 py-1 text-ink-900 bg-white"
            >
              <option value="price">Lowest price</option>
              <option value="rating">Best rated</option>
            </select>
          </label>
        )}
      </div>

      {prices.length === 0 ? (
        <p className="text-sm text-ink-500 py-6 text-center">No vendors listed yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-ink-900/5">
          {pageItems.map((p) => (
            <li key={p._id}>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 py-3 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-ink-900/5 flex items-center justify-center text-xs font-semibold uppercase text-ink-700 shrink-0">
                    {p.vendor.slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate group-hover:underline">{p.vendor}</div>
                    {typeof p.rating === 'number' && (
                      <StarRating rating={p.rating} count={p.ratingCount} />
                    )}
                    {!p.inStock && <div className="text-xs text-accent-red">Out of stock</div>}
                  </div>
                </div>
                <div
                  className={`font-data font-semibold whitespace-nowrap px-2 py-1 rounded-md ${
                    p.priceCents === bestPriceCents ? 'bg-accent-yellow/40' : ''
                  }`}
                >
                  {formatPrice(p.priceCents, p.currency)}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}

      <Pagination page={page_} totalPages={totalPages} onChange={setPage} className="mt-3" />
    </div>
  );
}
